import React, { useEffect, useMemo, useRef, useState } from "react";
import "./FindSpecialistPage.css";

// Leaflet map
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";

// Fix default marker icons in React bundlers
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

type CityOption = {
  CityID: number;
  Name: string;
  RegionID: number | null;
};

type SpecialtyOption = {
  SpecialtyID: number;
  Name: string;
  ParentSpecialtyID: number | null;
};

type CoverageTypeOption = {
  CoverageTypeID: number;
  Name: string;
};

type LanguageOption = {
  LanguageID: number;
  Name: string;
};

type ConditionOption = {
  ConditionID: number;
  Name: string;
};

type ProcedureOption = {
  ProcedureID: number;
  Name: string;
};

type Specialist = {
  SpecialistID: number;
  FullName: string;
  SpecialtyName?: string;
  ClinicName?: string;
  City?: string;
  ClinicPhone?: string;
  AcceptingNewReferrals: boolean;
  AgeGroup?: string;
  RoutineWeeks?: number | null;
  UrgentWeeks?: number | null;

  Latitude?: number | null;
  Longitude?: number | null;

  ClinicLatitude?: number | null;
  ClinicLongitude?: number | null;
};

const DEFAULT_MAP_CENTER: [number, number] = [43.6532, -79.3832]; // Toronto
const DEFAULT_MAP_ZOOM = 9;

function FitBounds({ points }: { points: Array<[number, number]> }) {
  const map = useMap();

  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => L.latLng(p[0], p[1])));
    map.fitBounds(bounds, { padding: [30, 30] });
  }, [map, points]);

  return null;
}

/** simple typeahead multiselect that adds on exact match selection */
function TypeaheadMultiSelect<T extends { [k: string]: any }>(props: {
  label: string;
  placeholder: string;
  options: T[];
  optionIdKey: keyof T;
  optionNameKey: keyof T;
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  inputId: string;
}) {
  const {
    label,
    placeholder,
    options,
    optionIdKey,
    optionNameKey,
    selectedIds,
    setSelectedIds,
    inputId,
  } = props;

  const [text, setText] = useState("");

  const selected = useMemo(() => {
    const set = new Set(selectedIds);
    return options.filter((o) => set.has(Number(o[optionIdKey])));
  }, [options, selectedIds, optionIdKey]);

  const addByName = (name: string) => {
    const match = options.find(
      (o) =>
        String(o[optionNameKey]).toLowerCase() === name.trim().toLowerCase()
    );
    if (!match) return;
    const id = Number(match[optionIdKey]);
    if (!Number.isFinite(id)) return;
    if (selectedIds.includes(id)) return;
    setSelectedIds([...selectedIds, id]);
  };

  const remove = (id: number) => {
    setSelectedIds(selectedIds.filter((x) => x !== id));
  };

  return (
    <div className="ocf-input-group">
      {label ? <label htmlFor={inputId}>{label}</label> : null}

      <input
        id={inputId}
        list={`${inputId}-list`}
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);

          const exact = options.find(
            (o) =>
              String(o[optionNameKey]).toLowerCase() === v.trim().toLowerCase()
          );
          if (exact) {
            addByName(v);
            setText("");
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (text.trim()) {
              addByName(text);
              setText("");
            }
          }
        }}
      />

      <datalist id={`${inputId}-list`}>
        {options.map((o) => (
          <option
            key={Number(o[optionIdKey])}
            value={String(o[optionNameKey])}
          />
        ))}
      </datalist>

      {selected.length > 0 && (
        <div className="ocf-chip-row" aria-label={`${label || "Selected"} items`}>
          {selected.map((o) => {
            const id = Number(o[optionIdKey]);
            return (
              <button
                key={id}
                type="button"
                className="ocf-chip"
                onClick={() => remove(id)}
                title="Remove"
              >
                {String(o[optionNameKey])} <span aria-hidden>×</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

type SortOption = "WaitTime" | "NameAZ" | "NameZA";

// Debounce helper
const debounce = (fn: () => void, delay = 300) => {
  let t: any;
  return () => {
    clearTimeout(t);
    t = setTimeout(() => fn(), delay);
  };
};

const FindSpecialistPage: React.FC = () => {
  // Main free-text search
  const [query, setQuery] = useState("");

  // Lookups
  const [cities, setCities] = useState<CityOption[]>([]);
  const [specialties, setSpecialties] = useState<SpecialtyOption[]>([]);
  const [coverageTypes, setCoverageTypes] = useState<CoverageTypeOption[]>([]);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [conditions, setConditions] = useState<ConditionOption[]>([]);
  const [procedures, setProcedures] = useState<ProcedureOption[]>([]);

  // Filters
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<number | null>(
    null
  );

  // typeahead inputs for single-selects
  const [citySearch, setCitySearch] = useState("");
  const [specialtySearch, setSpecialtySearch] = useState("");

  // Keep accepting checkbox under search bar (right column)
  const [acceptingOnly, setAcceptingOnly] = useState(true);

  // Multi-select filters
  const [ageGroups, setAgeGroups] = useState<string[]>([]);
  const [genders, setGenders] = useState<string[]>([]);
  const [visitTypes, setVisitTypes] = useState<string[]>([]);
  const [selectedCoverageTypeIds, setSelectedCoverageTypeIds] = useState<
    number[]
  >([]);

  const [selectedLanguageIds, setSelectedLanguageIds] = useState<number[]>([]);
  const [selectedConditionIds, setSelectedConditionIds] = useState<number[]>([]);
  const [selectedProcedureIds, setSelectedProcedureIds] = useState<number[]>([]);

  // Sort
  const [sortBy, setSortBy] = useState<SortOption>("WaitTime");

  // Results + UI state
  const [results, setResults] = useState<Specialist[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // used to suppress auto-search while a manual search is in-flight
  const searchingRef = useRef(false);

  // Load lookups once on mount
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [
          citiesRes,
          specialtiesRes,
          coverageRes,
          languagesRes,
          conditionsRes,
          proceduresRes,
        ] = await Promise.all([
          fetch("/api/cities"),
          fetch("/api/specialties"),
          fetch("/api/coverage-types"),
          fetch("/api/languages"),
          fetch("/api/conditions"),
          fetch("/api/procedures"),
        ]);

        const citiesData = await citiesRes.json();
        const specialtiesData = await specialtiesRes.json();
        const coverageData = await coverageRes.json();

        const languagesData = languagesRes.ok ? await languagesRes.json() : [];
        const conditionsData = conditionsRes.ok
          ? await conditionsRes.json()
          : [];
        const proceduresData = proceduresRes.ok
          ? await proceduresRes.json()
          : [];

        setCities(Array.isArray(citiesData) ? citiesData : []);
        setSpecialties(Array.isArray(specialtiesData) ? specialtiesData : []);
        setCoverageTypes(Array.isArray(coverageData) ? coverageData : []);
        setLanguages(Array.isArray(languagesData) ? languagesData : []);
        setConditions(Array.isArray(conditionsData) ? conditionsData : []);
        setProcedures(Array.isArray(proceduresData) ? proceduresData : []);
      } catch (err) {
        console.error("Failed to load lookup data", err);
      }
    };

    loadLookups();
  }, []);

  const handleCitySearchChange = (value: string) => {
    setCitySearch(value);
    if (!value) {
      setSelectedCityId(null);
      return;
    }
    const match = cities.find(
      (c) => c.Name.toLowerCase() === value.trim().toLowerCase()
    );
    setSelectedCityId(match ? match.CityID : null);
  };

  const handleSpecialtySearchChange = (value: string) => {
    setSpecialtySearch(value);
    if (!value) {
      setSelectedSpecialtyId(null);
      return;
    }
    const match = specialties.find(
      (s) => s.Name.toLowerCase() === value.trim().toLowerCase()
    );
    setSelectedSpecialtyId(match ? match.SpecialtyID : null);
  };

  const toggleString = (
    value: string,
    list: string[],
    setList: (v: string[]) => void
  ) => {
    setList(
      list.includes(value) ? list.filter((x) => x !== value) : [...list, value]
    );
  };

  const toggleCoverageId = (id: number) => {
    setSelectedCoverageTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const buildQueryString = () => {
    const params = new URLSearchParams();

    if (query.trim()) params.append("q", query.trim());
    if (selectedCityId !== null) params.append("cityId", String(selectedCityId));
    if (selectedSpecialtyId !== null)
      params.append("specialtyId", String(selectedSpecialtyId));
    if (acceptingOnly) params.append("acceptingNewReferrals", "true");

    // NOTE: your current API (/api/specialists/search) may not support these yet.
    // UI will still work; backend can be upgraded later.
    if (ageGroups.length > 0) params.append("ageGroups", ageGroups.join(","));
    if (genders.length > 0) params.append("genders", genders.join(","));
    if (visitTypes.length > 0) params.append("visitTypes", visitTypes.join(","));
    if (selectedCoverageTypeIds.length > 0)
      params.append("coverageTypeIds", selectedCoverageTypeIds.join(","));
    if (selectedLanguageIds.length > 0)
      params.append("languageIds", selectedLanguageIds.join(","));
    if (selectedConditionIds.length > 0)
      params.append("conditionIds", selectedConditionIds.join(","));
    if (selectedProcedureIds.length > 0)
      params.append("procedureIds", selectedProcedureIds.join(","));

    return params.toString();
  };

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setError(null);
    searchingRef.current = true;

    try {
      const qs = buildQueryString();
      const url = qs ? `/api/specialists/search?${qs}` : `/api/specialists/search`;

      const response = await fetch(url);
      if (!response.ok)
        throw new Error(`Request failed with status ${response.status}`);

      const data = (await response.json()) as Specialist[];
      setResults(data);
      setHasSearched(true);
    } catch (err) {
      console.error(err);
      setError("Something went wrong while searching. Please try again.");
    } finally {
      setIsLoading(false);
      searchingRef.current = false;
    }
  };

  const formatWaitTime = (routine?: number | null, urgent?: number | null) => {
    if (routine == null && urgent == null) return null;
    if (routine != null && urgent != null)
      return `Routine: ~${routine} weeks · Urgent: ~${urgent} weeks`;
    if (routine != null) return `Routine: ~${routine} weeks`;
    if (urgent != null) return `Urgent: ~${urgent} weeks`;
    return null;
  };

  const getCoords = (s: Specialist): { lat: number; lng: number } | null => {
    const lat =
      typeof s.Latitude === "number"
        ? s.Latitude
        : typeof s.ClinicLatitude === "number"
        ? s.ClinicLatitude
        : null;

    const lng =
      typeof s.Longitude === "number"
        ? s.Longitude
        : typeof s.ClinicLongitude === "number"
        ? s.ClinicLongitude
        : null;

    if (lat == null || lng == null) return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    return { lat, lng };
  };

  const mapPoints = useMemo(() => {
    return results
      .map((r) => getCoords(r))
      .filter((c): c is { lat: number; lng: number } => !!c)
      .map((c) => [c.lat, c.lng] as [number, number]);
  }, [results]);

  const mapCenter = useMemo<[number, number]>(() => {
    if (mapPoints.length) return mapPoints[0];
    return DEFAULT_MAP_CENTER;
  }, [mapPoints]);

  const sortedResults = useMemo(() => {
    const arr = [...results];

    if (sortBy === "NameAZ") {
      arr.sort((a, b) => (a.FullName || "").localeCompare(b.FullName || ""));
      return arr;
    }
    if (sortBy === "NameZA") {
      arr.sort((a, b) => (b.FullName || "").localeCompare(a.FullName || ""));
      return arr;
    }

    // WaitTime: sort by routine weeks ascending (nulls last), then urgent weeks
    arr.sort((a, b) => {
      const aRoutine = a.RoutineWeeks ?? Number.POSITIVE_INFINITY;
      const bRoutine = b.RoutineWeeks ?? Number.POSITIVE_INFINITY;
      if (aRoutine !== bRoutine) return aRoutine - bRoutine;

      const aUrg = a.UrgentWeeks ?? Number.POSITIVE_INFINITY;
      const bUrg = b.UrgentWeeks ?? Number.POSITIVE_INFINITY;
      return aUrg - bUrg;
    });
    return arr;
  }, [results, sortBy]);

  // Active filter count (for sidebar header)
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (selectedCityId !== null) n++;
    if (selectedSpecialtyId !== null) n++;
    if (ageGroups.length > 0) n++;
    if (genders.length > 0) n++;
    if (visitTypes.length > 0) n++;
    if (selectedCoverageTypeIds.length > 0) n++;
    if (selectedLanguageIds.length > 0) n++;
    if (selectedConditionIds.length > 0) n++;
    if (selectedProcedureIds.length > 0) n++;
    // acceptingOnly intentionally not counted (it’s a primary toggle under search)
    return n;
  }, [
    selectedCityId,
    selectedSpecialtyId,
    ageGroups,
    genders,
    visitTypes,
    selectedCoverageTypeIds,
    selectedLanguageIds,
    selectedConditionIds,
    selectedProcedureIds,
  ]);

  const clearFilters = () => {
    setSelectedCityId(null);
    setSelectedSpecialtyId(null);
    setCitySearch("");
    setSpecialtySearch("");

    setAgeGroups([]);
    setGenders([]);
    setVisitTypes([]);
    setSelectedCoverageTypeIds([]);

    setSelectedLanguageIds([]);
    setSelectedConditionIds([]);
    setSelectedProcedureIds([]);

    setAcceptingOnly(true);

    // keep query as-is (better UX)
  };

  // Auto-run search on filter changes (debounced)
  const debouncedAutoSearch = useMemo(() => {
    return debounce(() => {
      if (!hasSearched) return;
      if (searchingRef.current) return;
      handleSearch();
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasSearched,
    query,
    selectedCityId,
    selectedSpecialtyId,
    acceptingOnly,
    ageGroups,
    genders,
    visitTypes,
    selectedCoverageTypeIds,
    selectedLanguageIds,
    selectedConditionIds,
    selectedProcedureIds,
  ]);

  useEffect(() => {
    if (!hasSearched) return;
    debouncedAutoSearch();
  }, [
    hasSearched,
    query,
    selectedCityId,
    selectedSpecialtyId,
    acceptingOnly,
    ageGroups,
    genders,
    visitTypes,
    selectedCoverageTypeIds,
    selectedLanguageIds,
    selectedConditionIds,
    selectedProcedureIds,
    debouncedAutoSearch,
  ]);

  return (
    <div className="ocf-page">
      <header
        className="ocf-page-header"
        aria-labelledby="find-specialist-heading"
      >
        <div className="ocf-page-header-text">
          <h1 id="find-specialist-heading" className="ocf-page-title">
            Find a Specialist
          </h1>
          <p className="ocf-page-subtitle">
            Search by condition, specialty, or location to find the right
            specialist for your patient — quickly and clearly.
          </p>
        </div>
      </header>

      <main className="ocf-main">
        <section className="ocf-search-section" aria-label="Specialist search">
          <form className="ocf-search-form" onSubmit={handleSearch}>
            {/* Top search row (no overlap) */}
            <div className="ocf-searchbar-row">
              <div className="ocf-searchbar">
                <label className="ocf-sr-only" htmlFor="main-search-input">
                  Main search
                </label>
                <input
                  id="main-search-input"
                  className="ocf-searchbar-input"
                  type="text"
                  placeholder="Search by condition, specialist name, or keyword…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>

              <button
                type="submit"
                className="ocf-btn ocf-btn--primary ocf-btn--search"
                disabled={isLoading}
              >
                {isLoading ? "Searching…" : "Search"}
              </button>
            </div>

            {/* Keep accepting checkbox under search bar */}
            <div className="ocf-accepting-row">
              <label className="ocf-checkbox-inline">
                <input
                  type="checkbox"
                  checked={acceptingOnly}
                  onChange={(e) => setAcceptingOnly(e.target.checked)}
                />
                <span>Accepting new referrals only</span>
              </label>
            </div>

            {/* Two-column content: left filters / right map+results */}
            <div className="ocf-layout">
              {/* LEFT: Filters column */}
              <aside className="ocf-sidebar" aria-label="Filters">
                {/* Header row: Filters count + Clear button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                    gap: 10,
                  }}
                >
                  <strong>
                    Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                  </strong>

                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      className="ocf-btn ocf-btn--ghost"
                      onClick={clearFilters}
                      style={{ fontSize: "0.85rem", padding: "4px 10px" }}
                      aria-label="Clear all filters"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* City (single typeahead) */}
                <details className="ocf-filter-card" open>
                  <summary className="ocf-filter-summary">City</summary>
                  <div className="ocf-filter-body">
                    <div className="ocf-input-group">
                      <input
                        id="city-input"
                        list="city-options"
                        placeholder="Start typing a city…"
                        value={citySearch}
                        onChange={(e) => handleCitySearchChange(e.target.value)}
                      />
                      <datalist id="city-options">
                        {cities.map((c) => (
                          <option key={c.CityID} value={c.Name} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                </details>

                {/* Specialty (single typeahead) */}
                <details className="ocf-filter-card" open>
                  <summary className="ocf-filter-summary">Specialty</summary>
                  <div className="ocf-filter-body">
                    <div className="ocf-input-group">
                      <input
                        id="specialty-input"
                        list="specialty-options"
                        placeholder="Start typing a specialty…"
                        value={specialtySearch}
                        onChange={(e) => handleSpecialtySearchChange(e.target.value)}
                      />
                      <datalist id="specialty-options">
                        {specialties.map((s) => (
                          <option key={s.SpecialtyID} value={s.Name} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                </details>

                {/* Age group (multi) */}
                <details className="ocf-filter-card">
                  <summary className="ocf-filter-summary">Age group</summary>
                  <div className="ocf-filter-body">
                    <div
                      className="ocf-checkbox-list"
                      role="group"
                      aria-label="Age group"
                    >
                      {["Adult", "Pediatric", "Geriatric"].map((v) => (
                        <label key={v} className="ocf-checkbox-inline">
                          <input
                            type="checkbox"
                            checked={ageGroups.includes(v)}
                            onChange={() => toggleString(v, ageGroups, setAgeGroups)}
                          />
                          <span>{v}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </details>

                {/* Gender (multi) */}
                <details className="ocf-filter-card">
                  <summary className="ocf-filter-summary">
                    Preferred provider gender
                  </summary>
                  <div className="ocf-filter-body">
                    <div
                      className="ocf-checkbox-list"
                      role="group"
                      aria-label="Preferred provider gender"
                    >
                      {["Male", "Female", "Other"].map((v) => (
                        <label key={v} className="ocf-checkbox-inline">
                          <input
                            type="checkbox"
                            checked={genders.includes(v)}
                            onChange={() => toggleString(v, genders, setGenders)}
                          />
                          <span>{v}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </details>

                {/* Coverage (multi) */}
                <details className="ocf-filter-card">
                  <summary className="ocf-filter-summary">Coverage</summary>
                  <div className="ocf-filter-body">
                    <div className="ocf-checkbox-list" role="group" aria-label="Coverage">
                      {coverageTypes.map((ct) => (
                        <label key={ct.CoverageTypeID} className="ocf-checkbox-inline">
                          <input
                            type="checkbox"
                            checked={selectedCoverageTypeIds.includes(ct.CoverageTypeID)}
                            onChange={() => toggleCoverageId(ct.CoverageTypeID)}
                          />
                          <span>{ct.Name}</span>
                        </label>
                      ))}
                      {coverageTypes.length === 0 && (
                        <div className="ocf-muted">No coverage types loaded.</div>
                      )}
                    </div>
                  </div>
                </details>

                {/* Visit type (multi) */}
                <details className="ocf-filter-card">
                  <summary className="ocf-filter-summary">Visit type</summary>
                  <div className="ocf-filter-body">
                    <div className="ocf-checkbox-list" role="group" aria-label="Visit type">
                      {["InPerson", "Virtual", "Phone"].map((v) => (
                        <label key={v} className="ocf-checkbox-inline">
                          <input
                            type="checkbox"
                            checked={visitTypes.includes(v)}
                            onChange={() => toggleString(v, visitTypes, setVisitTypes)}
                          />
                          <span>
                            {v === "InPerson"
                              ? "In-person"
                              : v === "Virtual"
                              ? "Virtual"
                              : "Phone"}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </details>

                {/* Language (typeahead multiselect) */}
                <details className="ocf-filter-card">
                  <summary className="ocf-filter-summary">Language</summary>
                  <div className="ocf-filter-body">
                    <TypeaheadMultiSelect<LanguageOption>
                      label=""
                      placeholder="Type and select a language…"
                      options={languages}
                      optionIdKey="LanguageID"
                      optionNameKey="Name"
                      selectedIds={selectedLanguageIds}
                      setSelectedIds={setSelectedLanguageIds}
                      inputId="language-input"
                    />
                  </div>
                </details>

                {/* Condition (typeahead multiselect) */}
                <details className="ocf-filter-card">
                  <summary className="ocf-filter-summary">Condition</summary>
                  <div className="ocf-filter-body">
                    <TypeaheadMultiSelect<ConditionOption>
                      label=""
                      placeholder="Type and select a condition…"
                      options={conditions}
                      optionIdKey="ConditionID"
                      optionNameKey="Name"
                      selectedIds={selectedConditionIds}
                      setSelectedIds={setSelectedConditionIds}
                      inputId="condition-filter-input"
                    />
                  </div>
                </details>

                {/* Procedure (typeahead multiselect) */}
                <details className="ocf-filter-card">
                  <summary className="ocf-filter-summary">Procedure</summary>
                  <div className="ocf-filter-body">
                    <TypeaheadMultiSelect<ProcedureOption>
                      label=""
                      placeholder="Type and select a procedure…"
                      options={procedures}
                      optionIdKey="ProcedureID"
                      optionNameKey="Name"
                      selectedIds={selectedProcedureIds}
                      setSelectedIds={setSelectedProcedureIds}
                      inputId="procedure-input"
                    />
                  </div>
                </details>
              </aside>

              {/* RIGHT: Map + sort + results */}
              <div className="ocf-content">
                {error && (
                  <div className="ocf-alert" role="alert">
                    {error}
                  </div>
                )}

                {!isLoading && hasSearched && results.length === 0 && !error && (
                  <div className="ocf-empty">
                    <h2>No specialists found</h2>
                    <p>
                      Try broadening your search—reset filters or clear
                      city/specialty.
                    </p>
                  </div>
                )}

                {isLoading && (
                  <div className="ocf-loading" aria-live="polite">
                    Searching for specialists…
                  </div>
                )}

                {/* MAP */}
                {!isLoading && results.length > 0 && (
                  <div className="ocf-map-wrap" aria-label="Map of specialists">
                    <div className="ocf-map-header">
                      <h2 className="ocf-map-title">Map</h2>
                      <p className="ocf-map-subtitle">
                        Showing pins for specialists with known clinic
                        coordinates.
                      </p>
                    </div>

                    <div className="ocf-map">
                      <MapContainer
                        center={mapCenter}
                        zoom={DEFAULT_MAP_ZOOM}
                        scrollWheelZoom={true}
                        style={{ width: "100%", height: "100%" }}
                      >
                        <TileLayer
                          attribution="&copy; OpenStreetMap contributors"
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />

                        {mapPoints.length > 0 && <FitBounds points={mapPoints} />}

                        {results.map((s) => {
                          const c = getCoords(s);
                          if (!c) return null;

                          return (
                            <Marker
                              key={`pin-${s.SpecialistID}`}
                              position={[c.lat, c.lng]}
                            >
                              <Popup>
                                <div style={{ display: "grid", gap: 6 }}>
                                  <strong>{s.FullName}</strong>
                                  <div style={{ fontSize: 13, opacity: 0.85 }}>
                                    {s.SpecialtyName || "Specialist"}
                                    {s.City ? ` · ${s.City}` : ""}
                                  </div>
                                  {s.ClinicName && (
                                    <div style={{ fontSize: 13 }}>
                                      {s.ClinicName}
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    className="ocf-btn ocf-btn--ghost"
                                    onClick={() =>
                                      window.location.assign(
                                        `/portal/family-doctor/specialist/${s.SpecialistID}`
                                      )
                                    }
                                  >
                                    View profile
                                  </button>
                                </div>
                              </Popup>
                            </Marker>
                          );
                        })}
                      </MapContainer>
                    </div>

                    {mapPoints.length === 0 && (
                      <div className="ocf-map-note">
                        No coordinates returned yet — map pins will appear once
                        the API includes clinic latitude/longitude.
                      </div>
                    )}
                  </div>
                )}

                {/* Sort + Results header */}
                {!isLoading && results.length > 0 && (
                  <div className="ocf-results-toprow">
                    <div className="ocf-results-header">
                      <h2>
                        {sortedResults.length} specialist
                        {sortedResults.length === 1 ? "" : "s"} found
                      </h2>
                      <p>Use filters to narrow results.</p>
                    </div>

                    <div className="ocf-sort">
                      <label htmlFor="sort-by" className="ocf-sort-label">
                        Sort by
                      </label>
                      <select
                        id="sort-by"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as SortOption)}
                      >
                        <option value="WaitTime">Shortest wait-time</option>
                        <option value="NameAZ">Names A–Z</option>
                        <option value="NameZA">Names Z–A</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Results list */}
                {!isLoading && sortedResults.length > 0 && (
                  <ul className="ocf-results-list" aria-label="Specialist results">
                    {sortedResults.map((s) => {
                      const waitText = formatWaitTime(s.RoutineWeeks, s.UrgentWeeks);

                      return (
                        <li key={s.SpecialistID}>
                          <article className="ocf-card">
                            <div className="ocf-card-main">
                              <h3 className="ocf-card-title">{s.FullName}</h3>

                              <p className="ocf-card-subtitle">
                                {s.SpecialtyName || "Specialist"}
                                {s.City ? ` · ${s.City}` : ""}
                                {s.ClinicName ? ` · ${s.ClinicName}` : ""}
                              </p>

                              <div className="ocf-pill-row">
                                <span
                                  className={
                                    s.AcceptingNewReferrals
                                      ? "ocf-pill-status ocf-pill-status--open"
                                      : "ocf-pill-status ocf-pill-status--closed"
                                  }
                                >
                                  {s.AcceptingNewReferrals
                                    ? "Accepting new referrals"
                                    : "Not currently accepting new referrals"}
                                </span>

                                {s.AgeGroup && (
                                  <span className="ocf-pill-soft">
                                    Age group: {s.AgeGroup}
                                  </span>
                                )}
                              </div>

                              {s.ClinicPhone && (
                                <p className="ocf-card-meta">
                                  Phone:{" "}
                                  <a href={`tel:${s.ClinicPhone}`}>{s.ClinicPhone}</a>
                                </p>
                              )}

                              {waitText && (
                                <p className="ocf-card-meta">
                                  Estimated wait: {waitText}
                                </p>
                              )}
                            </div>

                            <div className="ocf-card-actions">
                              <button
                                type="button"
                                className="ocf-btn ocf-btn--ghost"
                                onClick={() =>
                                  window.location.assign(
                                    `/portal/family-doctor/specialist/${s.SpecialistID}`
                                  )
                                }
                              >
                                View profile
                              </button>
                            </div>
                          </article>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
};

export default FindSpecialistPage;
